import { PRIMENG_LICENSE_KEY } from '@pinnakl/shared/constants';
import { registerLicense } from '@primeui/license-manager';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

// Register the PrimeUI (PrimeNG) license once at module load — this module is imported
// by every app's `providePrimeNG` setup, before any Prime component renders.
registerLicense({ primeui: PRIMENG_LICENSE_KEY });

export const PinnaklPrimePreset = definePreset(Lara, {
  semantic: {
    // Lara's focus ring is already width 0 / style none, but it still paints a 0.2rem box-shadow
    // on focus. The token is consumed as `box-shadow: …`, so `none` — not `0` — turns it off.
    // Form fields inherit this through `{focus.ring.shadow}`, so they need no override of their own.
    focusRing: {
      shadow: 'none',
    },
    colorScheme: {
      light: {
        text: {
          color: '#262a32',
          mutedColor: '{textColorSecondary}',
        },
        iconSize: '1.25rem',
        formField: {
          borderRadius: '{border.radius.lg}',
          iconColor: '{textColor}',
        },
        primary: {
          color: '#168eff',
          hoverColor: '#006ace',
          activeColor: '#0076e5',
          500: '#168eff',
        },
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f6f6f6',
          200: '#ececec',
          300: '#dddddd',
          400: '#cbcbcb',
          500: '#aaaaaa',
          600: '#959595',
          700: '#6f6f6f',
          800: '#3c3c3c',
          900: '#292929',
        },
      },
    },
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            label: {
              fontWeight: '600',
            },
            paddingY: '0.5rem',
            paddingX: '1.5rem',
            sm: {
              paddingY: '0.25rem',
              paddingX: '0.5rem',
            },
            lg: {
              paddingY: '0.75rem',
              paddingX: '1.5rem',
            },
            secondary: {
              color: '{textColor}',
              background: '{bluegray.200}',
              borderColor: '{bluegray.200}',
              hoverColor: '{textColor}',
              hoverBackground: '{bluegray.400}',
              hoverBorderColor: '{bluegray.400}',
            },
            danger: {
              color: '{textColor}',
              background: '{red.600}',
              borderColor: '{red.600}',
              hoverColor: '{textColor}',
              hoverBackground: '{red.700}',
              hoverBorderColor: '{red.700}',
            },
          },
          text: {
            secondary: {
              color: '#292929',
            },
            primary: {
              hoverBackground: '{blue.50}',
              activeBackground: '{blue.100}',
            },
          },
          outlined: {
            primary: {
              borderColor: '{blue.600}',
              color: '{textColor}',
              hoverBackground: '{surface.0}',
            },
            secondary: {
              color: '{bluegray.900}',
              borderColor: '{bluegray.400}',
            },
          },
        },
      },
      // PrimeNG has no tokens for an outlined button's own background or its hover border/text,
      // so these three live in `extend`. Flat camelCase keys become `--p-button-outlined-*`
      // without replacing the `outlined` section Lara defines.
      extend: {
        outlinedPrimaryBackground: '{surface.0}',
        outlinedPrimaryHoverBorderColor: '{blue.700}',
        outlinedPrimaryHoverColor: '{textColor}',
      },
    },
    treetable: {
      colorScheme: {
        light: {
          headerCell: {
            selectedBackground: '#fff',
            selectedColor: '{blue.800}',
          },
          sortIcon: {
            size: '0.75rem',
          },
        },
      },
    },
    tree: {
      colorScheme: {
        light: {
          node: {
            selectedBackground: '{bluegray.200}',
            selectedColor: '{textColor}',
          },
          nodeToggleButton: {
            hoverColor: '{textColor}',
          },
        },
      },
    },
    tabs: {
      colorScheme: {
        light: {
          tab: {
            background: '{surface.0}',
            color: '{textColor}',
            padding: '0.75rem 1rem',
            fontWeight: '600',
            borderWidth: '0 0 2px 0',
            borderColor: '{gray.200}',
            hoverBackground: '{surface.0}',
            hoverBorderColor: '{textColor}',
          },
          tabpanel: {
            padding: '0',
          },
        },
      },
    },
    tooltip: {
      colorScheme: {
        light: {
          root: {
            background: '{bluegray.900}',
            color: '#ffffff',
          },
        },
      },
    },
    menubar: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            padding: '0',
            gap: '0',
          },
        },
      },
    },
    badge: {
      colorScheme: {
        light: {
          secondary: {
            background: '{gray.200}',
            color: '{textColor}',
          },
          warn: {
            background: '{orange.500}',
          },
        },
      },
      sm: {
        fontSize: '0.75rem',
      },
    },
    dialog: {
      colorScheme: {
        light: {
          root: {
            borderColor: 'transparent',
          },
          content: {
            padding: '0',
          },
        },
      },
    },
    popover: {
      colorScheme: {
        light: {
          root: {
            borderRadius: '8px',
          },
          content: {
            padding: '0',
          },
        },
      },
    },
    drawer: {
      colorScheme: {
        light: {
          header: {
            padding: '0',
          },
          content: {
            padding: '0',
          },
        },
      },
    },
    datepicker: {
      inputIcon: {
        color: '{textColor}',
      },
      date: {
        padding: '1px',
        width: '30px',
        height: '30px',
        borderRadius: '5px',
        selectedBackground: '{blue.100}',
        selectedColor: '{gray.600}',
        rangeSelectedBackground: '{blue.200}',
        rangeSelectedColor: '{gray.600}',
      },
    },
    inputtext: {
      colorScheme: {
        light: {
          root: {
            color: '{textColor}',
            hoverBorderColor: '{bluegray.700}',
            paddingY: '0.5rem',
            paddingX: '0.75rem',
            sm: {
              paddingY: '0.25rem',
              paddingX: '0.5rem',
            },
            lg: {
              paddingY: '0.75rem',
              paddingX: '0.5rem',
            },
            placeholderColor: '{bluegray.700}',
            // Declared outright rather than inherited via `{form.field.focus.ring.shadow}`, so the
            // variable itself reads `none` instead of pointing at another variable.
            focusRing: {
              shadow: 'none',
            },
          },
        },
      },
    },
    toggleswitch: {
      colorScheme: {
        light: {
          root: {
            width: '1.75rem',
            height: '1rem',
            gap: '0.125rem',
            background: '{bluegray.700}',
            hoverBackground: '{bluegray.800}',
            hoverBorderColor: '{bluegray.800}',
          },
          handle: {
            size: '0.75rem',
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          root: {
            width: '1rem',
            height: '1rem',
            borderColor: '{bluegray.700}',
          },
        },
      },
    },
    radiobutton: {
      colorScheme: {
        light: {
          root: {
            width: '1rem',
            height: '1rem',
            borderColor: '#7c7e83',
            hoverBorderColor: '#23282d',
            checkedBackground: '{form.field.background}',
            checkedHoverBackground: '{form.field.background}',
            checkedHoverBorderColor: '{primary.color}',
            disabledBackground: '{form.field.background}',
            checkedDisabledBorderColor: '{primary.color}',
          },
          icon: {
            size: '0.5rem',
            checkedColor: '{primary.color}',
            checkedHoverColor: '{primary.color}',
            disabledColor: '{primary.color}',
          },
        },
      },
    },
    autocomplete: {
      colorScheme: {
        light: {
          root: {
            placeholderColor: '{bluegray.700}',
            hoverBorderColor: '{bluegray.700}',
          },
          chip: {
            borderRadius: '1rem',
          },
          dropdown: {
            color: '{textColor}',
          },
        },
      },
    },
    multiselect: {
      colorScheme: {
        light: {
          root: {
            placeholderColor: '{bluegray.700}',
            hoverBorderColor: '{bluegray.700}',
          },
          dropdown: {
            color: '{textColor}',
          },
          option: {
            selectedColor: '{textColor}',
            selectedBackground: '{blue.200}',
            selectedFocusColor: '{textColor}',
            selectedFocusBackground: '{blue.300}',
          },
          overlay: {
            borderRadius: '{borderRadius.xl}',
          },
          chip: {
            borderRadius: '1rem',
          },
          clearIcon: {
            color: '{textColor}',
          },
        },
      },
    },
    select: {
      colorScheme: {
        light: {
          root: {
            color: '{textColor}',
            paddingY: '0.5rem',
            sm: {
              paddingY: '0.25rem',
            },
            lg: {
              paddingY: '0.75rem',
            },
            placeholderColor: '{bluegray.700}',
            hoverBorderColor: '{textColor}',
          },
          dropdown: {
            color: '{textColor}',
          },
          option: {
            focusBackground: '{blue.100}',
            selectedColor: '{textColor}',
            selectedBackground: '{blue.100}',
            selectedFocusColor: '{textColor}',
            selectedFocusBackground: '{blue.100}',
          },
        },
      },
    },
    togglebutton: {
      colorScheme: {
        light: {
          root: {
            padding: '0.5rem 1.5rem',
            sm: {
              padding: '0.25rem 0.5rem',
            },
            color: '{textColor}',
            hoverColor: '{textColor}',
          },
        },
      },
    },
    iconfield: {
      colorScheme: {
        light: {
          icon: {
            color: '{textColor}',
          },
        },
      },
    },
    chip: {
      root: {
        background: '{bluegray.300}',
      },
    },
    textarea: {
      colorScheme: {
        light: {
          root: {
            hoverBorderColor: '{textColor}',
          },
        },
      },
    },
  },
  // Tokens PrimeUIX has no slot for: our own `bluegray` palette, our overrides of the primitive
  // palettes, and the custom `--p-text-*` / `--p-link-*` variables the app's SCSS reads. `extend`
  // is the typed escape hatch for exactly this — each key becomes `--p-<kebab-case-of-the-path>`.
  extend: {
    textSecondary: '#646f85',
    textColorSecondary: '#646f85',
    linkColor: '{blue.600}',
    linkHoverColor: '{blue.700}',
    linkFocusColor: '{blue.700}',
    chartPrimaryColor: '{blue.600}',
    textLg: '1.25rem',
    textMd: '1rem',
    textSm: '0.875rem',
    textXs: '0.75rem',
    blue: {
      50: '#f7fbff',
      100: '#eef7ff',
      200: '#dceeff',
      300: '#c1e1ff',
      400: '#a0d1ff',
      500: '#59afff',
      600: '#2a98ff',
      700: '#006ed5',
      800: '#003c74',
      900: '#002950',
    },
    green: {
      50: '#f5fcf7',
      100: '#eaf9ef',
      200: '#d5f3df',
      300: '#b1e9c5',
      400: '#89dea7',
      500: '#36c167',
      600: '#30aa5a',
      700: '#247f44',
      800: '#134525',
      900: '#0d2f19',
    },
    red: {
      50: '#fff9f9',
      100: '#fff3f3',
      200: '#ffe7e7',
      300: '#ffd2d2',
      400: '#ffbaba',
      500: '#ff8686',
      600: '#ff5e5e',
      700: '#e10000',
      800: '#7f0000',
      900: '#5b0000',
    },
    orange: {
      50: '#fffaf4',
      100: '#fff4e8',
      200: '#ffe9d0',
      300: '#ffd6a7',
      400: '#ffbf76',
      500: '#ff8c09',
      600: '#e37900',
      700: '#aa5b00',
      800: '#5c3100',
      900: '#3f2200',
    },
    bluegray: {
      50: '#f9fafb',
      100: '#f4f5f7',
      200: '#eaecef',
      250: '#dddddd',
      300: '#dadde3',
      400: '#c7ccd5',
      500: '#a1a9b9',
      600: '#8b95a8',
      700: '#646f85',
      800: '#373d49',
      900: '#262a32',
    },
  },
});
